// src/pages/Products/ProductDetails.js
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { productService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import {
    PencilIcon,
    ArrowLeftIcon,
    PlusIcon,
    TrashIcon,
    CheckIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import ProductVariantForm from './ProductVariantForm';

const ProductDetails = () => {
    const { id } = useParams();
    const { hasAnyRole } = useAuth();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isVariantModalOpen, setIsVariantModalOpen] = useState(false);
    const [variants, setVariants] = useState([]);

    // State for inventory editing
    const [editingInventory, setEditingInventory] = useState(false);
    const [editedInventory, setEditedInventory] = useState([]);

    useEffect(() => {
        fetchProduct();
    }, [id]);

    useEffect(() => {
        // Initialize edited inventory when product data is loaded
        if (product && product.sku_inventory) {
            setEditedInventory([...product.sku_inventory]);
        }
    }, [product]);

    const fetchProduct = async () => {
        try {
            setLoading(true);
            const response = await productService.getProduct(id);
            setProduct(response.data);

            // Extract unique variants from part requirements
            if (response.data.part_requirements) {
                const uniqueVariants = [...new Set(
                    response.data.part_requirements.map(req => req.variant)
                )];
                setVariants(uniqueVariants);
            }

            setError(null);
        } catch (err) {
            console.error('Error fetching product details:', err);
            setError('Failed to load product details. Please try again later.');
            toast.error('Failed to load product details');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenVariantModal = () => {
        setIsVariantModalOpen(true);
    };

    const handleCloseVariantModal = () => {
        setIsVariantModalOpen(false);
    };

    const handleSaveVariant = async (variantData) => {
        try {
            // Ensure variant data has the correct structure
            if (!variantData.part_type_id || !variantData.quantity_required || !variantData.variant) {
                toast.error('Invalid variant data');
                return;
            }

            // Create a copy of the current product to add the new part requirement
            const updatedRequirements = product.part_requirements ?
                [...product.part_requirements] : [];

            // Add the new part requirement
            updatedRequirements.push(variantData);

            // Prepare the update payload
            const updatePayload = {
                ...product,
                part_requirements: updatedRequirements
            };

            // Update the product with the new part requirement
            const response = await productService.updateProduct(id, updatePayload);

            if (!response.success) {
                throw new Error(response.message || 'Failed to add variant');
            }

            toast.success('Variant added successfully');
            fetchProduct(); // Refresh data to show the new variant
        } catch (err) {
            console.error('Error saving variant:', err);
            toast.error('Failed to add variant: ' + (err.message || 'Unknown error'));
        }
    };

    const handleDeleteRequirement = async (requirementId) => {
        if (!window.confirm('Are you sure you want to delete this requirement? This cannot be undone.')) {
            return;
        }

        try {
            // Filter out the requirement to delete
            const updatedRequirements = product.part_requirements.filter(
                req => req.id !== requirementId
            );

            const updatedProduct = {
                ...product,
                part_requirements: updatedRequirements
            };

            // Update the product
            await productService.updateProduct(id, updatedProduct);

            toast.success('Requirement deleted successfully');
            fetchProduct(); // Refresh data
        } catch (err) {
            console.error('Error deleting requirement:', err);
            toast.error('Failed to delete requirement');
        }
    };

    // Handle inventory editing
    const handleStartEditingInventory = () => {
        setEditingInventory(true);
    };

    const handleCancelEditingInventory = () => {
        // Reset to original values
        setEditedInventory([...product.sku_inventory]);
        setEditingInventory(false);
    };

    const handleInventoryChange = (inventoryId, field, value) => {
        const updatedInventory = editedInventory.map(item => {
            if (item.id === inventoryId) {
                return { ...item, [field]: value };
            }
            return item;
        });
        setEditedInventory(updatedInventory);
    };

    // Function to prepare inventory data for saving
    const prepareInventoryForSave = () => {
        // Convert any empty string values to 0 before sending to the server
        return editedInventory.map(item => ({
            ...item,
            yield_per_part_type: item.yield_per_part_type === '' ? 0 : item.yield_per_part_type,
            current_stock: item.current_stock === '' ? 0 : item.current_stock,
            threshold: item.threshold === '' ? 0 : item.threshold,
            on_order: item.on_order === '' ? 0 : item.on_order
        }));
    };

    const handleSaveInventory = async () => {
        try {
            // Prepare inventory update payload with all empty values converted to 0
            const preparedInventory = prepareInventoryForSave();

            const updatePayload = {
                sku_inventory: preparedInventory
            };

            // Update the product with new inventory values
            const response = await productService.updateProduct(id, updatePayload);

            if (!response.success) {
                throw new Error(response.message || 'Failed to update inventory');
            }

            toast.success('Inventory updated successfully');
            setEditingInventory(false);
            fetchProduct(); // Refresh data
        } catch (err) {
            console.error('Error updating inventory:', err);
            toast.error('Failed to update inventory: ' + (err.message || 'Unknown error'));
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-md">
                <p className="font-bold">Error</p>
                <p>{error}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div className="flex items-center space-x-4">
                    <Link to="/products" className="text-gray-500 hover:text-gray-700">
                        <ArrowLeftIcon className="h-5 w-5" />
                    </Link>
                    <h1 className="text-2xl font-semibold text-gray-800">
                        Product: {product.sku}
                    </h1>
                </div>

                {hasAnyRole(['admin', 'procurement']) && (
                    <Link
                        to={`/products/${id}/edit`}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                        <PencilIcon className="h-5 w-5 mr-2" /> Edit Product
                    </Link>
                )}
            </div>

            {/* Product Details */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:px-6">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Product Details</h3>
                    <p className="mt-1 max-w-2xl text-sm text-gray-500">Product information and part requirements.</p>
                </div>
                <div className="border-t border-gray-200">
                    <dl>
                        <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-500">SKU</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{product.sku}</dd>
                        </div>
                        <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-500">Name</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{product.name}</dd>
                        </div>
                        <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-500">Description</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{product.description || 'No description'}</dd>
                        </div>
                        <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-500">Status</dt>
                            <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">
                                {product.is_active ? (
                                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                        Active
                                      </span>
                                ) : (
                                     <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                                        Inactive
                                      </span>
                                 )}
                            </dd>
                        </div>
                    </dl>
                </div>
            </div>

            {/* SKU-Specific Inventory */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
                    <div>
                        <h3 className="text-lg leading-6 font-medium text-gray-900">SKU-Specific Inventory</h3>
                        <p className="mt-1 max-w-2xl text-sm text-gray-500">
                            Current inventory of parts that are specific to this SKU.
                        </p>
                    </div>

                    {/* Edit/Save/Cancel buttons */}
                    {hasAnyRole(['admin', 'procurement']) && (
                        <div className="flex space-x-2">
                            {!editingInventory ? (
                                <button
                                    onClick={handleStartEditingInventory}
                                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                >
                                    <PencilIcon className="h-4 w-4 mr-1" /> Edit Inventory
                                </button>
                            ) : (
                                 <>
                                     <button
                                         onClick={handleSaveInventory}
                                         className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                     >
                                         <CheckIcon className="h-4 w-4 mr-1" /> Save
                                     </button>
                                     <button
                                         onClick={handleCancelEditingInventory}
                                         className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                     >
                                         <XMarkIcon className="h-4 w-4 mr-1" /> Cancel
                                     </button>
                                 </>
                             )}
                        </div>
                    )}
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                        <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Part Type
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Yield
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Current Stock
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Threshold
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                On Order
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                            </th>
                        </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                        {product.sku_inventory && product.sku_inventory.length > 0 ? (
                            editingInventory
                            ? editedInventory.map(item => (
                                <tr key={item.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {item.part_type?.name}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <input
                                            type="number"
                                            className="border rounded py-1 px-2 w-20 text-sm"
                                            value={item.yield_per_part_type === undefined ? '' : item.yield_per_part_type}
                                            onChange={(e) => {
                                                const value = e.target.value.trim();
                                                handleInventoryChange(
                                                    item.id,
                                                    'yield_per_part_type',
                                                    value === '' ? '' : parseInt(value) || 0
                                                );
                                            }}
                                            min="0"
                                        />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <input
                                            type="number"
                                            className="border rounded py-1 px-2 w-20 text-sm"
                                            value={item.current_stock === undefined ? '' : item.current_stock}
                                            onChange={(e) => {
                                                const value = e.target.value.trim();
                                                handleInventoryChange(
                                                    item.id,
                                                    'current_stock',
                                                    value === '' ? '' : parseInt(value) || 0
                                                );
                                            }}
                                            min="0"
                                        />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <input
                                            type="number"
                                            className="border rounded py-1 px-2 w-20 text-sm"
                                            value={item.threshold === undefined ? '' : item.threshold}
                                            onChange={(e) => {
                                                const value = e.target.value.trim();
                                                handleInventoryChange(
                                                    item.id,
                                                    'threshold',
                                                    value === '' ? '' : parseInt(value) || 0
                                                );
                                            }}
                                            min="0"
                                        />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <input
                                            type="number"
                                            className="border rounded py-1 px-2 w-20 text-sm"
                                            value={item.on_order === undefined ? '' : item.on_order}
                                            onChange={(e) => {
                                                const value = e.target.value.trim();
                                                handleInventoryChange(
                                                    item.id,
                                                    'on_order',
                                                    value === '' ? '' : parseInt(value) || 0
                                                );
                                            }}
                                            min="0"
                                        />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {(item.current_stock === '' || item.threshold === '' ||
                                          item.current_stock < item.threshold) ? (
                                             <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                              Low Stock
                                            </span>
                                         ) : (
                                             <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                                  Sufficient
                                                </span>
                                         )}
                                    </td>
                                </tr>
                            ))
                            : product.sku_inventory.map(item => (
                                <tr key={item.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {item.part_type?.name}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {item.yield_per_part_type}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {item.current_stock}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {item.threshold}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {item.on_order}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {item.current_stock < item.threshold ? (
                                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                              Low Stock
                                            </span>
                                        ) : (
                                             <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                                  Sufficient
                                                </span>
                                         )}
                                    </td>
                                </tr>
                            ))
                        ) : (
                             <tr>
                                 <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                                     No SKU-specific inventory items found.
                                 </td>
                             </tr>
                         )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Part Requirements */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
                    <div>
                        <h3 className="text-lg leading-6 font-medium text-gray-900">Part Requirements</h3>
                        <p className="mt-1 max-w-2xl text-sm text-gray-500">Parts required for manufacturing this product.</p>
                    </div>

                    {/* Add Variant button */}
                    {hasAnyRole(['admin', 'procurement']) && (
                        <button
                            onClick={handleOpenVariantModal}
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                            <PlusIcon className="h-4 w-4 mr-1" /> Add Variant
                        </button>
                    )}
                </div>

                {/* Display all part requirements grouped by variant */}
                {variants.map(variant => (
                    <div key={variant} className="border-t border-gray-200 px-4 py-5 sm:px-6">
                        <h4 className="text-md font-medium text-gray-900 capitalize">{variant} Variant</h4>
                        <div className="mt-4 overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Part Type
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Quantity Required
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        SKU-Specific
                                    </th>
                                    {hasAnyRole(['admin', 'procurement']) && (
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    )}
                                </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                {product.part_requirements && product.part_requirements.filter(req => req.variant === variant).length > 0 ? (
                                    product.part_requirements
                                           .filter(req => req.variant === variant)
                                           .map(requirement => (
                                               <tr key={`${variant}-${requirement.id}`}>
                                                   <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                       {requirement.part_type?.name}
                                                   </td>
                                                   <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                       {requirement.quantity_required}
                                                   </td>
                                                   <td className="px-6 py-4 whitespace-nowrap">
                                                       {requirement.part_type?.is_sku_specific ? (
                                                           <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                                              SKU-Specific
                                                            </span>
                                                       ) : (
                                                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                                                              General
                                                            </span>
                                                        )}
                                                   </td>
                                                   {hasAnyRole(['admin', 'procurement']) && (
                                                       <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                           <button
                                                               onClick={() => handleDeleteRequirement(requirement.id)}
                                                               className="text-red-600 hover:text-red-900"
                                                           >
                                                               <TrashIcon className="h-5 w-5" />
                                                           </button>
                                                       </td>
                                                   )}
                                               </tr>
                                           ))
                                ) : (
                                     <tr>
                                         <td colSpan={hasAnyRole(['admin', 'procurement']) ? "4" : "3"} className="px-6 py-4 text-center text-sm text-gray-500">
                                             No {variant} variant part requirements defined.
                                         </td>
                                     </tr>
                                 )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ))}
            </div>

            {/* Variant Modal */}
            {isVariantModalOpen && (
                <ProductVariantForm
                    isOpen={isVariantModalOpen}
                    onClose={handleCloseVariantModal}
                    onSave={handleSaveVariant}
                    productId={id}
                />
            )}
        </div>
    );
};

export default ProductDetails;
